/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import { ArrayExtensions, StringExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { getTelemetryObject, IModuleProps, INodeProps, ITelemetryContent, NodeTag } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { TileListLinks } from './components';
import { IHeadingData, ITileListItemProps } from './tile-list-item.props.autogenerated';

/**
 * Tile-list-item view props.
 */
export interface ITileListItemViewProps extends ITileListItemProps<{}> {
    heading?: React.ReactNode;
    paragraph?: React.ReactNode;
    links?: React.ReactNode;
    backgroundImage?: React.ReactNode;
    thumbnailImage?: React.ReactNode;
    tileListItem: IModuleProps;
    tileListContainer: INodeProps;
    tileListImageContainer: INodeProps;
    tileListThumbnailImageContainer: INodeProps;
    tileListHeadingContainer: INodeProps;
}

/**
 *
 * TileListItem component.
 * @extends {React.PureComponent<ITileListItemProps<{}>>}
 */
export class TileListItem extends React.PureComponent<ITileListItemProps<{}>> {
    private readonly _telemetryContent: ITelemetryContent = getTelemetryObject(
        this.props.context.request.telemetryPageName!,
        this.props.friendlyName,
        this.props.telemetry
    );

    public constructor(props: ITileListItemProps<{}>) {
        super(props);
    }

    public render(): JSX.Element {
        const { heading, paragraph, backgroundImage, thumbnailImage, redirectionUrl, links } = this.props.config;
        const tileListLinks = links && ArrayExtensions.hasElements(links) && (
            <TileListLinks
                links={links}
                onTextChange={this.handleLinkTextChange}
                requestContext={this.props.context.request}
                telemetryContent={this._telemetryContent}
            />
        );
        const tileListLinkContainer = {
            tag: 'a' as NodeTag,
            className: 'ms-tile-list-container',
            role: 'link',
            href: redirectionUrl?.linkUrl.destinationUrl,
            'aria-label': redirectionUrl?.ariaLabel
        };

        const tileListButtonContainer = {
            tag: 'button' as NodeTag,
            className: 'ms-tile-list-container'
        };
        const viewProps: ITileListItemViewProps = {
            ...this.props,
            heading: this._createHeading(heading),
            paragraph: this._createParagraph(paragraph),
            backgroundImage: this._renderBackgroundImage(backgroundImage),
            thumbnailImage: this._renderThumbnailImage(thumbnailImage),
            links: tileListLinks,
            tileListItem: {
                moduleProps: this.props,
                className: ''
            },
            tileListContainer: redirectionUrl ? tileListLinkContainer : tileListButtonContainer,
            tileListImageContainer: {
                className: 'ms-tile-list__image'
            },
            tileListThumbnailImageContainer: {
                className: 'ms-tile-list__thumbnail_image',
                'aria-hidden': 'true'
            },
            tileListHeadingContainer: {
                className: 'ms-tile-list__heading'
            }
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    /**
     * Handles heading content change event.
     * @param event - Content editable event.
     */
    public handleHeadingChange = (event: Msdyn365.ContentEditableEvent): void => {
        this.props.config.heading!.text = event.target.value;
    };

    /**
     * Handles paragraph content change event.
     * @param event - Content editable event.
     */
    public handleParagraphChange = (event: Msdyn365.ContentEditableEvent): void => {
        this.props.config.paragraph = event.target.value;
    };

    /**
     * Handles paragraph content change event.
     * @param linkIndex - Index of link in linkarray.
     * @returns Void.
     */
    public handleLinkTextChange = (linkIndex: number) => (event: Msdyn365.ContentEditableEvent): void => {
        if (this.props.config.links?.[Number(linkIndex)]) {
            this.props.config.links[Number(linkIndex)].linkText = event.target.value;
        }
    };

    private _createParagraph(text?: Msdyn365.RichText): React.ReactNode | null {
        if (!text) {
            return null;
        }
        return (
            <Msdyn365.RichTextComponent
                text={text}
                className='ms-tile-list__text'
                editProps={{ onEdit: this.handleParagraphChange, requestContext: this.props.context.request }}
            />
        );
    }

    private _createHeading(heading?: IHeadingData): React.ReactNode | null {
        if (!heading) {
            return null;
        }
        if (StringExtensions.isNullOrWhitespace(heading.text)) {
            return null;
        }

        return (
            <Msdyn365.Text
                tag={heading.tag ?? 'h1'}
                text={heading.text}
                className='ms-tile-list__title'
                editProps={{ onEdit: this.handleHeadingChange, requestContext: this.props.context.request }}
            />
        );
    }

    private _renderBackgroundImage(item?: Msdyn365.IImageData, isAriaHiddenAttribute?: boolean): React.ReactNode | null {
        // If image is null, we want to render the placeholder SVG - by passing image data with empty src

        const defaultImageSettings: Msdyn365.IImageSettings = {
            viewports: {
                xs: { q: 'w=328&h=412&m=6', w: 328, h: 412 },
                sm: { q: 'w=328&h=412&m=6', w: 328, h: 412 },
                md: { q: 'w=315&h=412&m=6', w: 315, h: 412 },
                lg: { q: 'w=315&h=412&m=6', w: 315, h: 412 },
                xl: { q: 'w=315&h=412&m=6', w: 315, h: 412 }
            },
            lazyload: true
        };
        const verifiedImageSettings = item?.imageSettings?.viewports ? item.imageSettings : defaultImageSettings;

        return (
            <Msdyn365.Image
                {...item}
                requestContext={this.props.context.request}
                gridSettings={this.props.context.request.gridSettings!}
                imageSettings={verifiedImageSettings}
                loadFailureBehavior='default'
                aria-hidden={isAriaHiddenAttribute}
                editProps={{
                    key: this.props.config.backgroundImage ?? {},
                    requestContext: this.props.context.request,
                    moduleType: this.props.typeName,
                    imagePropertyName: 'image'
                }}
            />
        );
    }

    private _renderThumbnailImage(
        item?: Msdyn365.IImageData,
        itemImageSettings?: Msdyn365.IImageSettings,
        isAriaHiddenAttribute?: boolean
    ): React.ReactNode | null {
        if (!item) {
            return null;
        }
        if (StringExtensions.isNullOrWhitespace(item.src)) {
            return null;
        }

        return (
            <Msdyn365.Image
                {...item}
                gridSettings={this.props.context.request.gridSettings!}
                imageSettings={itemImageSettings}
                loadFailureBehavior='default'
                aria-hidden={isAriaHiddenAttribute}
                editProps={{
                    key: this.props.config.thumbnailImage ?? {},
                    requestContext: this.props.context.request,
                    moduleType: this.props.typeName,
                    imagePropertyName: 'image',
                    // eslint-disable-next-line max-len -- Auto-suppressed.
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment -- Auto-suppressed.
                    layout: (this.props.config as any).msdyn365__moduleLayout
                }}
            />
        );
    }
}

export default TileListItem;
